import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import type { PageIdentifier } from 'hb-react/shared/constants/pageIdentifiers';
import { usePageConfigQuery } from 'hb-react/shared/hooks/usePageConfigQuery';

import { getNameFromMenu, getTopicPageIdentifier } from './utils';

/**
 * Hook that returns the menu name of the page. It first tries to get the name
 * from the menu, and if it's not found, it fetches the page_customizable
 * endpoint.
 *
 * @param identifier {string} The identifier of the page. NOT feature_name!!!
 * @param defaultName {string} Fallback name if page for some reason is not found.
 * @param options {object} Options object.
 * @returns {string} The name of the page, or empty string if it's still loading.
 */
export const usePageMenuName = (
  identifier: PageIdentifier,
  defaultName: string,
  options: { enabled: boolean } = { enabled: true },
): string => {
  const { topic, topicId: globalTopicId, whitelabelURL } = useTopicContext();
  // @ts-expect-error untyped context value
  const topicId = globalTopicId || topic?.id;

  const normalizedIdentifier = topicId
    ? getTopicPageIdentifier(identifier)
    : identifier;

  const networkMenu = useCachedNetworkMenu();
  const topicMenu = useCachedTopicMenu(topicId);

  // Get the name of the page from the menu...
  const nameFromMenu = useMemo(
    () =>
      getNameFromMenu(topicId ? topicMenu : networkMenu, normalizedIdentifier),
    [topicId, networkMenu, normalizedIdentifier, topicMenu],
  );

  // ...or fetch it from the page_customizable endpoint.
  const { isLoading, data: pageConfig } = usePageConfigQuery({
    identifier,
    topicId,
    whitelabelURL,
    enabled: !nameFromMenu && !!identifier && options.enabled,
  });

  return useMemo(() => {
    if (!options.enabled) {
      return '';
    }

    if (!identifier) {
      return defaultName;
    }

    if (nameFromMenu) {
      return nameFromMenu;
    }

    if (isLoading) {
      return '';
    }

    return pageConfig?.menu_name || defaultName || '';
  }, [
    defaultName,
    identifier,
    isLoading,
    nameFromMenu,
    options.enabled,
    pageConfig,
  ]);
};

/**
 * Helper hook to get the network menu from the cache.
 */
const useCachedNetworkMenu = () => {
  const { currentNetwork } = useDefaultAppContexts();
  const queryClient = useQueryClient();

  return currentNetwork?.app_pages || queryClient.getQueryData(['appPages']);
};

/**
 * Helper hook to get the topic menu from the query cache if in the topic context.
 */
const useCachedTopicMenu = (topicId) => {
  const queryClient = useQueryClient();

  return topicId
    ? queryClient.getQueryData(['topic', topicId, 'menu'])
    : undefined;
};

export { PAGES } from 'hb-react/shared/constants/pageIdentifiers';
